import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import Icon from "components/Icon";
import CookieBanner from "components/CookieBanner";
import NavBar from "components/NavBar";
import SeloGooglePlay from "components/SeloGooglePlay";
import SeletorCidade from "components/SeletorCidade";
import { appAndroidPublicado } from "config/appAndroid";
import { textoDeVenda } from "config/textosDeVenda";
import useRevelarAoRolar from "hooks/useRevelarAoRolar";
import ItemPergunta from "pages/Faq/ItemPergunta";
import { pegarPerguntas } from "pages/Faq/faqDef";
import { ehAndroidNoNavegador } from "services/plataforma";
import { temSessao } from "services/sessao";
import TarjaApp from "./TarjaApp";

// Atraso da cascata entre itens vizinhos de uma mesma grade. Com teto porque a partir do quarto
// item o visitante já leu o primeiro: sem o teto, uma lista que cresça acabaria com o último card
// esperando quase um segundo, e aí a animação vira lentidão. O CSS só aplica o atraso do sm para
// cima, onde os itens estão lado a lado (src/styles/index.css).
const atrasoDaCascata = (indice) => ({ "--revelar-atraso": `${Math.min(indice, 3) * 80}ms` });

// Fotos do hero. Eram `background-image` com URL do images.unsplash.com: uma origem a mais na
// primeira pintura, 1440px em qualquer tela e invisíveis para o navegador até o CSS ser aplicado.
// Agora são arquivos do próprio site (public/images/hero/, WebP, licença Unsplash), em quatro
// larguras, num <img> com srcset — o celular baixa ~30 KB em vez de ~220 KB.
//
// Desde o hero de duas portas cada foto é a faixa de cima de um cartão: a coluna inteira menos o
// respiro lateral no celular, metade do max-w-6xl do md para cima. Os px fixos são essa metade.
//
// O SRCSET/SIZES da primeira foto se repetem no <script> de preload do public/index.html: o
// navegador só reaproveita o download se os dois forem idênticos. Mudou aqui, muda lá.
const larguras = [640, 960, 1280, 1920];
const srcsetHero = (nome) =>
	larguras.map((w) => `/images/hero/hero-${nome}-${w}.webp ${w}w`).join(", ");
const SIZES_HERO = "(min-width: 1152px) 530px, (min-width: 768px) 46vw, calc(100vw - 32px)";

/**
 * O selo sobre a foto de cada porta: o mesmo pedido visto dos dois lados — "Pedido confirmado"
 * para quem contrata, "Novo pedido de faxina" para quem trabalha. É ilustração, não dado, por isso
 * aria-hidden. Sem data de propósito, pelo mesmo motivo do PEDIDO_EXEMPLO: envelheceria. No celular
 * fica só o título; a linha de baixo volta do md para cima, onde a foto tem altura para ela.
 */
function SeloPedido({ titulo, icone, corIcone, className = "" }) {
	return (
		<span
			aria-hidden="true"
			className={`absolute bottom-2.5 flex items-center gap-2 rounded-full bg-surface py-1.5 pl-1.5 pr-3 text-xs font-bold text-dark shadow-lg md:bottom-4 md:gap-2.5 md:rounded-2xl md:py-2 md:pl-2 md:pr-3.5 ${className}`}
		>
			<span className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-white md:h-8 md:w-8 md:rounded-xl ${corIcone}`}>
				<Icon name={icone} className="h-3.5 w-3.5 md:h-4 md:w-4" />
			</span>
			<span className="leading-tight">
				<span className="block md:text-[0.8125rem]">{titulo}</span>
				<span className="hidden text-xs font-normal text-muted md:block">Sábado, 8h, Centro</span>
			</span>
		</span>
	);
}

// Amostra das dúvidas que travam a decisão, três para cada uma das duas portas do hero. Vêm
// separadas por público e com rótulo, pelo mesmo motivo que o faqDef separa os grupos: numa lista
// corrida o visitante teria que filtrar mentalmente quais das seis perguntas são dele. O texto
// vem de faqDef para não duplicar.
const PERGUNTAS_DESTAQUE = [
	{
		rotulo: "Quem contrata",
		perguntas: pegarPerguntas(
			"quanto-custa-para-contratar",
			"como-funciona-o-agendamento",
			"como-sao-avaliados-os-profissionais"
		),
	},
	{
		// As três objeções que travam o cadastro e que a seção de passos não resolve: o preço,
		// como se paga e cancela a assinatura, e se isso cria vínculo empregatício.
		rotulo: "Quem trabalha",
		perguntas: pegarPerguntas(
			"quanto-custa-para-trabalhar",
			"formas-de-pagamento-assinatura",
			"vinculo-empregaticio"
		),
	},
];

// Fotos das seções abaixo do hero (public/images/home/). Vieram dos protótipos da Vercel: o
// recorte de cada card, ampliado, com o número do passo que vinha gravado na imagem apagado e o
// logo da Nova Faxina bordado no uniforme (no lugar do "LIMP LAR" e dos logos que o gerador
// inventou). O tratamento está em prompts/home-com-fotos.md.
//
// Mesmo esquema do hero: WebP em larguras fixas num <img> com srcset, para o celular baixar a
// menor. Aqui com loading="lazy", porque nenhuma delas está na primeira dobra. `width`/`height`
// são os da maior largura; o que importa para o CLS é a proporção, e o aspect-* do contêiner já a
// fixa antes do download.
const FORMATOS_FOTO = {
	quadrada: { larguras: [400, 700, 1000], altura: (w) => w },
	larga: { larguras: [480, 800, 1376], altura: (w) => Math.round(w * 0.75) },
	retrato: { larguras: [520, 1028], altura: (w) => Math.round(w * 1.25) },
};
// Cada `sizes` acompanha a grade em que a foto mora: 2 colunas no celular e 4 do lg para cima
// (passos), miniatura de 88px no celular e 3 colunas do md para cima (vantagens), coluna inteira e
// meia largura do lg para cima (retrato). Os px fixos são a coluna dentro do max-w-6xl.
const SIZES_PASSO = "(min-width: 1152px) 264px, (min-width: 1024px) 23vw, 48vw";
const SIZES_VANTAGEM = "(min-width: 1152px) 352px, (min-width: 768px) 31vw, 88px";
const SIZES_RETRATO = "(min-width: 1152px) 520px, (min-width: 1024px) 45vw, 100vw";

function FotoHome({ nome, formato, sizes, alt, className = "" }) {
	const { larguras, altura } = FORMATOS_FOTO[formato];
	const maior = larguras[larguras.length - 1];
	return (
		<img
			src={`/images/home/${nome}-${larguras[0]}.webp`}
			srcSet={larguras.map((w) => `/images/home/${nome}-${w}.webp ${w}w`).join(", ")}
			sizes={sizes}
			width={maior}
			height={altura(maior)}
			loading="lazy"
			decoding="async"
			alt={alt}
			className={`h-full w-full object-cover ${className}`}
		/>
	);
}

// "Como agendar" descreve o mesmo fluxo da resposta "como-funciona-o-agendamento" do faqDef,
// mas com texto próprio, porque os dois públicos são outros: aqui o visitante ainda não viu o
// sistema, então nome de tela ("Aguardando confirmação", "Minha agenda") não tem referente e lê
// como burocracia; no FAQ é justamente o que faz a pessoa achar o botão.
//
// Uma linha por passo. A versão anterior tinha um parágrafo de 25 a 35 palavras em cada card, e
// no celular eles ficavam recolhidos atrás de uma seta: oito parágrafos empilhados viravam um
// paredão, e o que o visitante escaneava era a sequência de títulos. Agora quem conta o passo é a
// foto; o texto só fecha a ideia. Fluxo mudou? Mexer aqui E em faqDef ('como-funciona-o-agendamento').
const PASSOS_AGENDAMENTO = [
	{
		titulo: "Escolha sua cidade",
		descricao: "Veja diaristas perto de você, com preço e estrelas.",
		foto: "agendar-1",
		alt: "Mãos segurando o celular com o mapa da cidade aberto",
	},
	{
		titulo: "Marque dia e hora",
		descricao: "Faxina completa + capricho extra em até 3 pontos.",
		foto: "agendar-2",
		alt: "Cliente sorrindo enquanto escolhe a data da faxina no celular",
	},
	{
		titulo: "Envie o pedido",
		descricao: "Um clique. Sem telefonema e sem custo.",
		foto: "agendar-3",
		alt: "Dedo tocando o botão de enviar o pedido na tela do celular",
	},
	{
		titulo: "Receba o “sim”",
		descricao: "Confirmado na agenda. É só abrir a porta.",
		foto: "agendar-4",
		alt: "Cliente abrindo a porta de casa para a profissional",
	},
];

// Os três argumentos de "Por que contratar pela Nova Faxina", todos com respaldo em faqDef
// ("quanto-custa-para-contratar", "como-sao-avaliados-os-profissionais",
// "posso-cancelar-um-agendamento") — nada de adjetivo genérico que o concorrente também assina.
const VANTAGENS_CONTRATAR = [
	{
		titulo: "Contratar é fácil",
		descricao: "Sem taxa, mensalidade ou comissão. O pagamento é feito diretamente à profissional.",
		icone: "sell",
		foto: "vantagem-1",
		alt: "Cliente relaxando no sofá de uma sala limpa, com uma caneca nas mãos",
	},
	{
		titulo: "Avaliações reais",
		descricao: "Só avalia quem teve o serviço concluído.",
		icone: "star",
		foto: "vantagem-2",
		alt: "Cliente apertando a mão da profissional da Nova Faxina",
	},
	{
		titulo: "Você manda na agenda",
		descricao: "Escolha o dia e cancele com um botão.",
		icone: "event_available",
		foto: "vantagem-3",
		alt: "Cliente conferindo a agenda no celular, sentada na poltrona",
	},
];

// As garantias de "Sua agenda, seus preços, seus clientes", ao lado do retrato. Os valores
// acompanham as respostas de faqDef ("quanto-custa-para-trabalhar", "como-recebo-pedidos",
// "formas-de-pagamento-assinatura").
//
// "Comece grátis" é o plano Livre (C5b); o Pro é opcional. O preço do Pro é literal no cartão "Quero
// trabalhar", de propósito: esta é a página de SEO, e pendurar uma requisição a /planos nela para um
// número que muda uma vez por ano custaria mais do que resolve. A contrapartida é manual — quem
// reajustar o plano em /admin/financeiro/planos tem que passar por esse cartão, pelas respostas do
// FAQ e por config/textosDeVenda (faqQuantoCusta).
const GARANTIAS_PROFISSIONAL = ["Comece grátis, sem comissão", "Aceite só o que couber", "Sem fidelidade"];

// O aviso de "Novo pedido" que fica sobre o retrato. É ilustração, não dado: por isso aria-hidden
// e botões que são <span>, sem foco nem clique. Os campos são os que a profissional vê de verdade
// antes de confirmar (faqDef "como-recebo-pedidos": dia, bairro, serviços), e o prazo é a regra
// real — a confirmação vale até 23:59 da véspera. Sem data no "Quando" de propósito: um dia do mês
// escrito aqui envelheceria na primeira semana.
const PEDIDO_EXEMPLO = [
	["Quando", "Sábado, 8h"],
	["Onde", "Centro"],
	["Serviço", "Faxina completa + janelas"],
];

function AvisoPedidoExemplo() {
	return (
		<div
			aria-hidden="true"
			className="absolute bottom-3 left-3 w-52 rounded-2xl bg-surface p-3 shadow-xl ring-1 ring-dark/5 sm:w-72 sm:p-4 lg:-left-7 lg:bottom-9"
		>
			<div className="flex items-center gap-2.5">
				<span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-white">
					<Icon name="notifications" className="h-5 w-5" />
					<span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-white" />
				</span>
				<span className="leading-tight">
					<span className="block text-sm font-semibold text-dark">
						Novo pedido<span className="hidden sm:inline"> de faxina</span>
					</span>
					<span className="block text-xs text-muted">
						<span className="sm:hidden">Até sexta, 23:59</span>
						<span className="hidden sm:inline">Responda até sexta, 23:59</span>
					</span>
				</span>
			</div>
			{/* No celular o aviso encolhe (w-52) e perde os detalhes: no recorte horizontal da foto,
			    inteiro, ele cobriria o logo bordado no peito. Título e botões bastam para dizer "o
			    pedido chega e você decide". */}
			<dl className="mt-3 hidden grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[0.8125rem] sm:grid">
				{PEDIDO_EXEMPLO.map(([rotulo, valor]) => (
					<Fragment key={rotulo}>
						<dt className="text-muted">{rotulo}</dt>
						<dd className="font-semibold text-dark">{valor}</dd>
					</Fragment>
				))}
			</dl>
			<div className="mt-3 flex gap-2 text-[0.8125rem] font-semibold">
				<span className="flex-1 rounded-full bg-primary py-2 text-center text-white">Aceitar</span>
				<span className="flex-1 rounded-full bg-subtle py-2 text-center text-dark">Agora não</span>
			</div>
		</div>
	);
}

// O "como funciona" do outro lado do balcão. As garantias acima entregam o porquê (preço, sem
// comissão, sem fidelidade) e esta lista entrega o como: sem ela, o profissional nunca lê na Home
// o que acontece depois do clique — que é justamente onde mora a fricção dele, já que paga a
// assinatura antes de faturar o primeiro serviço.
//
// O passo 3 não repete o preço de propósito: o valor aparece a poucos pixels daqui, e cada cópia
// literal a mais é outro ponto para esquecer no dia do reajuste.
const PASSOS_PROFISSIONAL = [
	{
		titulo: "Crie seu perfil",
		descricao: "Seus dados e a cidade onde você atende.",
		foto: "prof-1",
		alt: "Profissional de uniforme da Nova Faxina tirando a foto do perfil com o celular",
	},
	{
		titulo: "Defina preço e dias",
		descricao: "Por pacote de horas ou por dia, nos dias que quiser.",
		foto: "prof-2",
		alt: "Profissional anotando numa agenda os dias em que vai trabalhar",
	},
	{
		titulo: "Comece no Livre",
		descricao: textoDeVenda("homePassoPlano"),
		foto: "prof-3",
		alt: "Profissional ativando o perfil pelo celular",
	},
	{
		titulo: "Aceite e receba",
		descricao: "Você decide. O valor combinado é todo seu.",
		foto: "prof-4",
		alt: "Profissional de uniforme limpando a bancada da cozinha",
	},
];

// Seção "Dicas de limpeza" (vídeos do YouTube) desativada temporariamente a pedido —
// futuramente será reativada. Ver bloco comentado no fim do JSX, mais o estado
// `videosAbertos`/`abrirVideo` abaixo, também comentados junto.
// const VIDEOS_DICAS = [
// 	{ id: "v2bAdjIrZJ4", titulo: "Dicas de limpeza — parte 1" },
// 	{ id: "btDy9e7dZXk", titulo: "Dicas de limpeza — parte 2" },
// ];

/**
 * Um passo do "como funciona", nas duas seções (quem contrata e quem trabalha): foto quadrada com
 * o número por cima, título e uma linha.
 *
 * Também no celular a grade fica em 2 colunas, com o mesmo card. A versão anterior virava linha
 * abaixo do sm e escondia o parágrafo atrás de uma seta; com uma linha só de texto não há o que
 * recolher, e a foto é o que faz a sequência se ler de relance.
 *
 * O número fica no canto da foto, no mesmo lugar em que estava gravado na imagem original — o que
 * havia ali foi apagado por difusão, e o selo cobre o que sobrou. Ele é aria-hidden porque o <ol>
 * já numera os itens para o leitor de tela. Posição por top/left, e não por translate: o
 * `transform` do selo é o do pop de entrada (.revelar-selo).
 */
function CardPasso({ passo, indice }) {
	const { titulo, descricao, foto, alt } = passo;

	return (
		<li
			data-revelar
			style={atrasoDaCascata(indice)}
			className="card group flex flex-col overflow-hidden p-0 transition duration-300 hover:shadow-lg"
		>
			<div className="relative aspect-square overflow-hidden bg-primary/5">
				<FotoHome
					nome={foto}
					formato="quadrada"
					sizes={SIZES_PASSO}
					alt={alt}
					className="transition duration-500 group-hover:scale-105"
				/>
				<span
					aria-hidden="true"
					className="revelar-selo absolute left-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white shadow-md sm:left-3 sm:top-3 sm:h-11 sm:w-11 sm:text-base"
				>
					{indice + 1}
				</span>
			</div>
			<div className="flex flex-1 flex-col p-3 sm:p-5">
				<h3 className="text-base font-semibold leading-snug text-dark sm:text-lg">{titulo}</h3>
				<p className="mt-1 text-sm leading-relaxed text-muted sm:text-base">{descricao}</p>
			</div>
		</li>
	);
}

/**
 * Um argumento de "Por que contratar pela Nova Faxina": foto, título e uma linha.
 *
 * No celular é uma linha de lista, com a foto em miniatura de 88px ao lado do texto. Era um
 * carrossel com a ponta do próximo card à vista, mas numa página que se lê rolando para baixo
 * pouca gente desliza para o lado, e dois dos três argumentos ficavam sem leitura. Em lista os
 * três cabem numa tela.
 *
 * Do md para cima volta a ser card de grade, com a foto em 3:2 (era 4:3, e a foto ocupava três
 * quartos do card, abafando o argumento). O ícone fica na emenda da foto com o texto, dentro de
 * um anel de `surface` que o separa da foto; no celular ele sai, porque a miniatura já ancora a
 * linha.
 */
function CardVantagem({ vantagem, indice }) {
	const { titulo, descricao, icone, foto, alt } = vantagem;

	return (
		<li
			data-revelar
			style={atrasoDaCascata(indice)}
			className="card group grid grid-cols-[88px_minmax(0,1fr)] items-center gap-4 p-3 transition duration-300 md:flex md:flex-col md:items-stretch md:gap-0 md:overflow-hidden md:p-0 md:hover:shadow-lg"
		>
			<div className="aspect-square overflow-hidden rounded-xl bg-primary/5 md:aspect-[3/2] md:rounded-none">
				<FotoHome
					nome={foto}
					formato="larga"
					sizes={SIZES_VANTAGEM}
					alt={alt}
					className="transition duration-500 md:group-hover:scale-105"
				/>
			</div>
			<div className="relative md:px-6 md:pb-6 md:pt-9">
				<span className="absolute -top-[26px] left-6 hidden rounded-full bg-surface p-1 md:block">
					<span className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary">
						<Icon name={icone} className="h-6 w-6" />
					</span>
				</span>
				<h3 className="text-lg font-semibold leading-snug text-dark">{titulo}</h3>
				<p className="mt-1 text-base text-muted">{descricao}</p>
			</div>
		</li>
	);
}

export default function Inicio() {
	const [perguntasAbertas, setPerguntasAbertas] = useState([]);
	// Sem useMetadadosPagina de propósito: o título e a description da Home são os do próprio
	// public/index.html, que é também o que o hook devolve quando as outras páginas desmontam.
	// A página é longa e densa; sem ritmo o visitante recebe o paredão inteiro no primeiro quadro.
	// O hero fica fora da revelação de propósito: é o LCP da página.
	useRevelarAoRolar();
	// Os embeds do YouTube só entram no DOM depois do clique: dois iframes carregados de
	// imediato custam ~1 MB e dezenas de requisições de terceiros na última dobra da Home.
	// Comentado junto com a seção "Dicas do youtube", desativada temporariamente.
	// const [videosAbertos, setVideosAbertos] = useState([]);

	const alternarPergunta = (id) =>
		setPerguntasAbertas((atual) =>
			atual.includes(id) ? atual.filter((aberta) => aberta !== id) : [...atual, id]
		);

	// Leitura direta do localStorage, sem state, igual ao NavBar e ao Footer: a Home remonta a
	// cada troca de rota, então login/logout já refletem aqui.
	const logado = temSessao();

	// Os CTAs de "quero trabalhar" apontavam sempre para /usuario/novo. Para quem já tem conta
	// isso é um beco: o wizard é público, e o cliente logado que clicava aqui travava no
	// checkcpf com o próprio CPF. Logado, o destino é a ativação do perfil — que preserva
	// avaliações, histórico e verificação já aprovada em vez de pedir um cadastro novo.
	const ctaProfissional = logado
		? { to: "/profissional/ativar", state: undefined, rotulo: "Criar meu perfil" }
		: { to: "/usuario/novo", state: { tipoCadastroInicial: "profissional" }, rotulo: "Criar meu perfil" };

	// const abrirVideo = (id) => setVideosAbertos((atual) => [...atual, id]);

	// Cidade do campo da porta "Quero contratar" (só do md para cima). Guarda o município inteiro, e
	// não só o código, para o SeletorCidade ter o rótulo se remontar.
	//
	// Lê e grava os mesmos cookies da busca (pages/Profissional, 7 dias): quem já escolheu a cidade,
	// aqui ou lá, volta à Home com o campo preenchido. Sem cookie de rótulo, o SeletorCidade hidrata
	// o nome pelo código.
	const navigate = useNavigate();
	const [cidadeHero, setCidadeHero] = useState(() => {
		const codigo = Cookies.get("cidadeSelecionada");
		return codigo
			? { codigo, uf: Cookies.get("estadoSelecionado") || "", rotulo: Cookies.get("cidadeEstadoSelecionada") || "" }
			: null;
	});
	const trocarCidadeHero = (municipio) => {
		setCidadeHero(municipio);
		if (!municipio) {
			Cookies.remove("estadoSelecionado");
			Cookies.remove("cidadeSelecionada");
			Cookies.remove("cidadeEstadoSelecionada");
			return;
		}
		Cookies.set("estadoSelecionado", municipio.uf, { expires: 7 });
		Cookies.set("cidadeSelecionada", municipio.codigo, { expires: 7 });
		Cookies.set("cidadeEstadoSelecionada", municipio.rotulo, { expires: 7 });
	};
	const buscarProfissionais = (evento) => {
		evento.preventDefault();
		navigate(cidadeHero ? `/profissional?cidade=${cidadeHero.codigo}` : "/profissional");
	};

	return (
		<>
			<NavBar />
			{/* Convite para o app Android: só no navegador de um Android. Por que abaixo do menu, e
			    não acima, está no próprio componente. */}
			<TarjaApp />

			{/* Hero — duas portas do mesmo tamanho. O split-screen anterior tinha as duas escolhas,
			    mas com a foto esmaecida atrás de um degradê e o texto em halo branco; e a proposta de
			    "busca primeiro" que veio depois deixava quem trabalha numa faixa abaixo da busca, como
			    fazem Uber, TaskRabbit e GetNinjas. Aqui a receita vem da assinatura do profissional, e
			    essa porta não pode parecer de segunda classe: os dois cartões têm o mesmo tamanho, e o
			    de quem trabalha é navy para não se confundir com o outro. Proposta e alternativas no
			    canvas citado em prompts/hero-duas-portas.md. */}
			<section className="bg-background pb-10 pt-5 md:pb-14 md:pt-10">
				<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
					{/* Um título para os dois públicos, e não um por porta: é ele que diz, antes de
					    qualquer cartão, que o site serve aos dois lados. Traz "diarista" duas vezes de
					    propósito, que é o termo que as pessoas digitam na busca; "faxineira" vem logo
					    abaixo, no cartão de quem contrata. */}
					<h1 className="mx-1 text-[1.6875rem] font-extrabold leading-tight tracking-tight text-dark md:mx-0 md:text-center md:text-[2.625rem]">
						Contrate uma diarista ou trabalhe como diarista
					</h1>
					<p className="mx-1 mt-2 text-[0.9375rem] leading-relaxed text-muted md:mx-0 md:mt-2.5 md:text-center md:text-lg">
						Clientes e profissionais da mesma cidade se encontram aqui, sem comissão no meio.
					</p>

					<div className="relative mt-[18px] flex flex-col gap-3 md:mt-8 md:grid md:grid-cols-2 md:gap-7">
						{/* Sem overflow-hidden no cartão: a lista do SeletorCidade abre para baixo dele e
						    seria cortada. Quem arredonda a foto é o próprio contêiner dela. */}
						<section aria-labelledby="porta-contratar" className="flex flex-col rounded-[20px] border-caixa border-border bg-surface shadow-caixa md:rounded-3xl">
							<div className="relative h-28 overflow-hidden rounded-t-[20px] bg-subtle md:h-48 md:rounded-t-3xl">
								{/* fetchpriority alta só nesta: é a primeira foto da página. */}
								<img
									src="/images/hero/hero-contratar-960.webp"
									srcSet={srcsetHero("contratar")}
									sizes={SIZES_HERO}
									width="1920"
									height="1280"
									fetchpriority="high"
									alt="Sala de estar limpa e arrumada, com sofá de couro e plantas"
									className="h-full w-full object-cover object-[40%_70%]"
								/>
								<SeloPedido titulo="Pedido confirmado" icone="check" corIcone="bg-success" className="left-2.5 md:left-4" />
							</div>
							<div className="flex flex-1 flex-col p-4 md:px-7 md:pb-6 md:pt-5">
								<h2 id="porta-contratar" className="text-[1.3125rem] font-extrabold leading-snug text-dark md:text-[1.75rem]">
									Quero contratar
								</h2>
								<p className="mt-1 text-sm leading-relaxed text-dark/75 md:text-base">
									Diaristas e faxineiras da sua cidade, com preço e avaliação à mostra. Contratar é grátis.
								</p>
								{/* No celular só o botão: o campo de cidade empurraria a porta de quem trabalha
								    para fora da primeira tela, e a busca já pede a cidade logo na chegada. */}
								<Link to="/profissional" className="btn-primary btn-lg mt-4 w-full justify-center md:hidden">
									Encontrar profissionais <Icon name="arrow_forward" className="h-4 w-4" />
								</Link>
								{/* Do md para cima a cidade já sai daqui: ?cidade=<IBGE> é o mesmo parâmetro
								    que o aviso de push usa (pages/Profissional). Sem cidade escolhida, a busca
								    abre na do cookie, como sempre abriu. */}
								<form onSubmit={buscarProfissionais} className="mt-auto hidden pt-5 md:flex md:flex-col md:gap-3 lg:flex-row lg:items-end">
									<SeletorCidade
										id="cidade-hero"
										value={cidadeHero?.codigo || ""}
										rotuloInicial={cidadeHero?.rotulo || ""}
										onChange={trocarCidadeHero}
										label="Onde vai ser a faxina?"
										placeholder="Digite sua cidade"
										className="min-w-0 flex-1"
									/>
									<button type="submit" className="btn-primary justify-center py-3 text-base">
										Ver profissionais <Icon name="arrow_forward" className="h-4 w-4" />
									</button>
								</form>
							</div>
						</section>

						{/* "ou": no celular fica no vão entre os cartões, sobre a emenda; do md para cima,
						    no centro da grade, entre as duas colunas. */}
						<span
							aria-hidden="true"
							className="relative z-10 mx-auto -my-6 grid h-9 w-9 place-items-center rounded-full border border-gray-200 bg-surface text-[0.8125rem] font-bold text-muted shadow-md md:absolute md:left-1/2 md:top-1/2 md:m-0 md:h-12 md:w-12 md:-translate-x-1/2 md:-translate-y-1/2 md:text-[0.9375rem]"
						>
							ou
						</span>

						<section aria-labelledby="porta-trabalhar" className="flex flex-col overflow-hidden rounded-[20px] bg-dark text-white shadow-caixa md:rounded-3xl">
							<div className="relative h-28 bg-dark md:h-48">
								<img
									src="/images/hero/hero-trabalhar-960.webp"
									srcSet={srcsetHero("trabalhar")}
									sizes={SIZES_HERO}
									width="1920"
									height="1280"
									alt="Profissional de limpeza com vassoura e balde de produtos numa cozinha"
									className="h-full w-full object-cover object-[50%_40%]"
								/>
								<SeloPedido titulo="Novo pedido de faxina" icone="notifications" corIcone="bg-primary" className="right-2.5 md:right-4" />
							</div>
							<div className="flex flex-1 flex-col p-4 md:px-7 md:pb-6 md:pt-5">
								<h2 id="porta-trabalhar" className="text-[1.3125rem] font-extrabold leading-snug md:text-[1.75rem]">
									Quero trabalhar
								</h2>
								<p className="mt-1 text-sm leading-relaxed text-white/80 md:text-base">
									Receba pedidos de faxina da sua cidade e aceite só o que couber na sua agenda.
								</p>
								<div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-4 md:pt-5">
									{/* Literal — ver a nota em GARANTIAS_PROFISSIONAL. */}
									<p className="leading-tight">
										<span className="block text-[1.375rem] font-extrabold md:text-[1.625rem]">Comece grátis</span>
										<span className="block text-[0.8125rem] text-white/80 md:text-sm">
											Pro por R$ 7,90/mês<span className="hidden md:inline">, sem fidelidade</span>
										</span>
									</p>
									<Link
										to={ctaProfissional.to}
										state={ctaProfissional.state}
										className="btn justify-center bg-surface px-5 py-3 text-base text-dark hover:bg-subtle focus:ring-white focus:ring-offset-dark md:px-6"
									>
										{ctaProfissional.rotulo} <Icon name="arrow_forward" className="h-4 w-4" />
									</Link>
								</div>
							</div>
						</section>
					</div>
				</div>
			</section>

			{/* Como agendar — responde "o que acontece depois que eu clicar?", a objeção que vem
			    logo após o hero em serviço presencial. Por isso é a primeira dobra rolada. */}
			<section className="py-16 sm:py-20 bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-10 sm:mb-12" data-revelar>
						<span className="t-sobre-marca mb-2 block">
							Simples assim
						</span>
						<h2 className="text-3xl md:text-4xl font-bold text-dark">
							Como agendar uma faxina
						</h2>
					</div>

					{/* 2 colunas até o lg, 4 dali para cima — ver o CardPasso, mais acima. */}
					<ol className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
						{PASSOS_AGENDAMENTO.map((passo, indice) => (
							<CardPasso key={passo.titulo} passo={passo} indice={indice} />
						))}
					</ol>

					<div className="mt-10 sm:mt-12 text-center" data-revelar>
						<Link
							to="/profissional"
							className="btn-primary btn-lg transform hover:scale-105 shadow-lg"
						>
							Encontrar profissionais <Icon name="arrow_forward" className="h-4 w-4" />
						</Link>
					</div>
				</div>
			</section>

			{/* Proposta de valor — argumentos concretos e verificáveis (todos com respaldo em
			    faqDef) no lugar de adjetivos genéricos, que não diferenciam de concorrente nenhum.
			    O rosa a 5% é o fundo das faixas alternadas: o branco puro emendaria com as seções
			    vizinhas, e o bg-light (#fafafa) cinza esfriava as fotos. */}
			<section className="py-16 sm:py-20 bg-primary/5">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					{/* text-balance e o espaço inseparável: sem eles o título quebrava no celular com
					    "Faxina" sozinha na segunda linha. */}
					<div className="text-center mb-8 sm:mb-12" data-revelar>
						<span className="t-sobre-marca mb-2 block">
							Para quem contrata
						</span>
						<h2 className="text-3xl md:text-4xl font-bold text-dark text-balance">
							Por que contratar pela Nova&nbsp;Faxina
						</h2>
					</div>

					{/* Lista no celular, grade de 3 do md para cima — ver o CardVantagem. */}
					<ul className="grid gap-3 md:grid-cols-3 md:gap-6">
						{VANTAGENS_CONTRATAR.map((vantagem, indice) => (
							<CardVantagem key={vantagem.titulo} vantagem={vantagem} indice={indice} />
						))}
					</ul>

					{/* A seção termina num próximo passo: quem se convenceu aqui não precisa voltar
					    até o botão de "Como agendar". Largura total no celular, onde é alvo de polegar. */}
					<div className="mt-8 sm:mt-10 md:text-center" data-revelar>
						<Link
							to="/profissional"
							className="btn-primary btn-lg w-full justify-center shadow-lg md:w-auto"
						>
							Encontrar profissionais <Icon name="arrow_forward" className="h-4 w-4" />
						</Link>
					</div>
				</div>
			</section>

			{/*
			 * Espaço reservado para prova social (depoimentos e avaliações reais de clientes).
			 * É o lugar natural dela na jornada: logo depois dos argumentos e antes das objeções.
			 * Fica vazio de propósito enquanto não houver avaliações verdadeiras para exibir —
			 * número inventado aqui vira publicidade enganosa e, se o visitante perceber, sinaliza
			 * plataforma vazia, que é o efeito oposto ao pretendido.
			 */}

			{/* Para profissionais — o hero promete dois caminhos, então o lado da oferta precisa de
			    espaço próprio. Sem isto, quem clicou em "Quero Trabalhar" e rolou não acha mais nada.
			    A foto é a identificação ("essa pessoa sou eu"); o aviso de pedido por cima dela mostra
			    como o trabalho chega e que é a profissional quem decide. No celular a foto sobe para o
			    topo (order-first), num recorte horizontal: quem rola se vê antes de ler. */}
			<section className="py-16 sm:py-20 bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-16">
						<div data-revelar>
							<span className="t-sobre-marca mb-2 block">
								Para quem trabalha
							</span>
							<h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 max-w-md">
								Sua agenda, seus preços, seus clientes
							</h2>
							<p className="t-lead max-w-md">
								Você define quanto cobra e quando trabalha. O cliente paga direto a você.
							</p>

							{/* Garantias em linha, com check, e não blocos: os quatro blocos rosados que
							    estavam aqui tinham cara de botão e não faziam nada. */}
							<ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2.5">
								{GARANTIAS_PROFISSIONAL.map((garantia) => (
									<li key={garantia} className="inline-flex items-center gap-2 font-medium text-dark/90">
										<Icon name="check" className="h-5 w-5 flex-shrink-0 text-success" />
										{garantia}
									</li>
								))}
							</ul>

							{/* A frase ao lado do botão responde ao medo de pagar antes de faturar: a conta
							    nasce na etapa 5 do Usuario/Novo, sem cobrança, e a assinatura é a etapa 6 —
							    quem para ali fica PENDENTE e paga depois. Mudou o funil? Mexer aqui. */}
							<div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
								<Link
									to={ctaProfissional.to}
									state={ctaProfissional.state}
									className="btn-primary btn-lg justify-center transform hover:scale-105 shadow-lg"
								>
									{ctaProfissional.rotulo} <Icon name="arrow_forward" className="h-4 w-4" />
								</Link>
								<p className="text-sm leading-snug text-muted sm:max-w-[16rem]">
									Criar o perfil é grátis. A assinatura é o último passo, e é ela que coloca
									você na busca.
								</p>
							</div>
						</div>

						{/* Entra logo depois do texto, não junto: a foto é a prova do que o texto acabou
						    de prometer. */}
						<div
							data-revelar
							style={{ "--revelar-atraso": "120ms" }}
							className="relative order-first lg:order-none"
						>
							<div className="aspect-[4/3] overflow-hidden rounded-3xl bg-primary/5 shadow-caixa lg:aspect-[4/5]">
								<FotoHome
									nome="retrato"
									formato="retrato"
									sizes={SIZES_RETRATO}
									alt="Profissional da Nova Faxina, de uniforme com o logo bordado, segurando um balde com produtos de limpeza"
									className="object-[50%_22%]"
								/>
							</div>
							<AvisoPedidoExemplo />
						</div>
					</div>
				</div>
			</section>

			{/* Como funciona para quem trabalha — o par do "Como agendar", do outro lado do balcão.
			    Fica colada na seção acima (mesmo rótulo "Para quem trabalha") para que as duas leiam
			    como um capítulo só: lá o porquê, aqui o como. */}
			<section className="py-16 sm:py-20 bg-primary/5">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-10 sm:mb-12" data-revelar>
						<span className="t-sobre-marca mb-2 block">
							Para quem trabalha
						</span>
						<h2 className="text-3xl md:text-4xl font-bold text-dark">
							Do cadastro ao primeiro pedido
						</h2>
					</div>

					<ol className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
						{PASSOS_PROFISSIONAL.map((passo, indice) => (
							<CardPasso key={passo.titulo} passo={passo} indice={indice} />
						))}
					</ol>

					<div className="mt-10 sm:mt-12 text-center" data-revelar>
						<Link
							to={ctaProfissional.to}
							state={ctaProfissional.state}
							className="btn-primary btn-lg transform hover:scale-105 shadow-lg"
						>
							{ctaProfissional.rotulo} <Icon name="arrow_forward" className="h-4 w-4" />
						</Link>
					</div>
				</div>
			</section>

			{/* Perguntas frequentes em destaque. Do lg para cima os dois públicos ficam lado a lado,
			    cada um na sua coluna: empilhados, quem trabalha tinha que passar pelas três perguntas
			    de quem contrata para chegar nas suas. */}
			<section className="py-16 sm:py-20 bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-10 sm:mb-12" data-revelar>
						<span className="t-sobre-marca mb-2 block">
							Transparência
						</span>
						<h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
							Perguntas frequentes
						</h2>
						<p className="t-lead max-w-2xl mx-auto">
							Sem letras miúdas, dos dois lados.
						</p>
					</div>

					{/* O rótulo do grupo é um span, não um heading: cada pergunta já entra como <h3>
					    (ItemPergunta), e um heading de mesmo nível aqui quebraria a hierarquia. */}
					{/* A revelação para no grupo: o ItemPergunta é compartilhado com a página do FAQ e
					    marcá-lo levaria a animação para lá junto. */}
					<div className="grid gap-10 lg:grid-cols-2 lg:gap-8">
						{PERGUNTAS_DESTAQUE.map(({ rotulo, perguntas }, indice) => (
							<div key={rotulo} data-revelar style={atrasoDaCascata(indice)}>
								<span className="t-sobre-marca mb-3 block">
									{rotulo}
								</span>
								<div className="space-y-3">
									{perguntas.map((item) => (
										<ItemPergunta
											key={item.id}
											item={item}
											aberta={perguntasAbertas.includes(item.id)}
											onAlternar={alternarPergunta}
										/>
									))}
								</div>
							</div>
						))}
					</div>

					<div className="mt-10 text-center" data-revelar>
						<Link
							to="/perguntas-frequentes"
							className="inline-flex items-center text-primary font-semibold hover:text-primaryHover transition group"
						>
							Ver todas as perguntas
							<Icon name="arrow_forward" className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition" />
						</Link>
					</div>
				</div>
			</section>

{/* Quem Somos — institucional converte pouco, então fica depois do conteúdo de decisão,
			    como reforço de confiança já perto do fechamento. */}
			{/*
			<section className="py-16 bg-light">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col lg:flex-row items-center gap-16">
						<div className="lg:w-1/2" data-revelar>
							<img
								className="w-full h-auto rounded-2xl shadow-2xl object-cover aspect-video"
								src="https://storage.googleapis.com/uxpilot-auth.appspot.com/09a354255e-3006a262dc2e6f5edfdf.png"
								alt="Equipe de profissionais de limpeza sorrindo"
								loading="lazy"
							/>
						</div>
						<div className="lg:w-1/2" data-revelar style={{ "--revelar-atraso": "120ms" }}>
							<span className="t-sobre-marca mb-2 block">
								Nossa História
							</span>
							<h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">Quem Somos</h2>
							<p className="text-lg text-muted mb-6 leading-relaxed">
								A Nova Faxina nasceu em 2025 com um propósito claro: resolver a dificuldade de encontrar
								profissionais de limpeza confiáveis e, ao mesmo tempo, oferecer oportunidades dignas e
								flexíveis para quem busca trabalhar nessa área.
							</p>
							<p className="text-lg text-muted mb-8 leading-relaxed">
								Acreditamos que um ambiente limpo transforma a qualidade de vida das pessoas. Por isso,
								construímos um ecossistema seguro e tecnológico que valoriza o trabalho dos profissionais
								e entrega tranquilidade para as famílias e empresas brasileiras.
							</p>
							<Link
								to="/quemsomos"
								className="inline-flex items-center text-primary font-semibold hover:text-primaryHover transition group"
							>
								Conheça mais sobre nós
								<Icon name="arrow_forward" className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition" />
							</Link>
						</div>
					</div>
				</div>
			</section>
			*/}

			{/* Dicas do youtube — oculta temporariamente a pedido (2026-08-05), sem conteúdo de
			    vídeo suficiente por ora. Futuramente será reativada: basta descomentar este bloco
			    e os trechos irmãos comentados mais acima (import de FaCirclePlay, VIDEOS_DICAS,
			    o estado videosAbertos e a função abrirVideo). */}
			{/*
			<section className="py-20 bg-white">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-12">
						<h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">Dicas de Limpeza</h2>
						<p className="text-lg text-muted leading-relaxed">
							Confira com nosso parceiro no YouTube dicas práticas e truques de limpeza para facilitar o seu dia a dia.
						</p>
					</div>

					<div className="flex flex-col md:flex-row gap-8 max-w-4xl mx-auto">
						{VIDEOS_DICAS.map(({ id, titulo }) => (
							<div key={id} className="flex-1 aspect-video">
								{videosAbertos.includes(id) ? (
									<iframe
										className="w-full h-full rounded-2xl shadow-lg"
										src={`https://www.youtube.com/embed/${id}?autoplay=1`}
										title={titulo}
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
										referrerPolicy="strict-origin-when-cross-origin"
										allowFullScreen
									></iframe>
								) : (
									<button
										type="button"
										onClick={() => abrirVideo(id)}
										aria-label={`Reproduzir vídeo: ${titulo}`}
										className="group relative w-full h-full overflow-hidden rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
									>
										<img
											src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
											alt=""
											loading="lazy"
											className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
										/>
										<span className="absolute inset-0 bg-dark/30 transition group-hover:bg-dark/40"></span>
										<span className="absolute inset-0 flex items-center justify-center text-white text-6xl drop-shadow-lg transition group-hover:scale-110">
											<Icon name="play_circle" className="h-6 w-6" />
										</span>
									</button>
								)}
							</div>
						))}
					</div>
				</div>
			</section>
			*/}

			{/* Fechamento — a última dobra antes do rodapé devolve as duas escolhas do hero para
			    quem leu a página inteira. É um card rosa sobre o branco, e não mais uma faixa de
			    largura total: com o rodapé agora navy, uma faixa escura aqui emendaria nele e o
			    convite se perderia como o começo do rodapé. Os dois círculos são o desenho do logo,
			    só como textura. */}
			<section className="py-16 sm:py-20 bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="relative overflow-hidden rounded-[2rem] bg-primary px-6 py-14 text-center sm:px-12 sm:py-16">
						<span aria-hidden="true" className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-white/10" />
						<span aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-12 h-48 w-48 rounded-full bg-white/10" />

						<h2 className="relative text-3xl md:text-4xl font-bold text-white" data-revelar>
							Pronto para começar?
						</h2>
						<p className="relative mx-auto mt-3 max-w-md text-lg text-white/90 leading-relaxed" data-revelar style={atrasoDaCascata(1)}>
							Contrate uma diarista ou comece a atender clientes da sua cidade.
						</p>

						<div className="relative mt-8 flex flex-col sm:flex-row gap-3 justify-center" data-revelar style={atrasoDaCascata(2)}>
							<Link
								to="/profissional"
								className="btn btn-lg justify-center bg-white text-primary hover:bg-light focus:ring-white transform hover:scale-105 shadow-lg"
							>
								Encontrar profissionais <Icon name="arrow_forward" className="h-4 w-4" />
							</Link>
							<Link
								to={ctaProfissional.to}
								state={ctaProfissional.state}
								className="btn btn-lg justify-center border-2 border-white text-white hover:bg-white hover:text-primary focus:ring-white transform hover:scale-105"
							>
								{ctaProfissional.rotulo} <Icon name="arrow_forward" className="h-4 w-4" />
							</Link>
						</div>

						{/* O app como terceira saída, abaixo dos dois botões e menor que eles: quem leu
						    a página inteira pode preferir continuar pelo celular. Vale para qualquer
						    sistema, como o selo do rodapé; a linha legal da marca já está lá, nesta
						    mesma página. */}
						{appAndroidPublicado() && (
							<div className="relative mt-8 flex flex-col items-center gap-2" data-revelar style={atrasoDaCascata(3)}>
								<span className="text-sm font-medium text-white/90">Ou baixe o app</span>
								<SeloGooglePlay origem="fechamento-home" className="h-12" novaAba={!ehAndroidNoNavegador()} />
							</div>
						)}
					</div>
				</div>
			</section>

			<CookieBanner />
		</>
	);
}
